import * as React from 'react';
import { filter, flow, get, isEmpty } from 'lodash';
import { FieldArray, formValueSelector, getFormSyncErrors, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { IAuthState } from '../../../../services/auth/models';
import { IProduct, IQuotes, ITraveller } from '../../../../data/api/models';
import { II18nMessages } from '../../../../services/branding/models';
import { IQuoteActions, ISignpostState } from '../../../../services/quote/models';
import { ValidationFailed } from '../Components/ValidationFailed';
import { InfoAnnie } from '../../components/InfoAnnie';
import { Form } from 'react-bootstrap';
import { QuoteStepButtons } from '../Components/QuoteStepButtons';
import { MedicalQuestionCards } from './Components/MedicalQuestionCards';
import TravellersMedical from '../../components/TravellersMedical';
import confirm from 'react-confirm2';
import mapMedicalQuestions from '../../../../helpers/medical';
import { scrollToTop } from '../../../../helpers/TravelApplication';
import { IVeriskState } from '../../../../services/verisk/models';
import { WarningMessages } from '../Verisk/scenes/WarningMessages';

interface IMedicalDeclarationQuestionsProps extends InjectedFormProps {
  auth: IAuthState;
  travellers: ITraveller[];
  quotes: IQuotes;
  product: IProduct;
  i18n: II18nMessages;
  synchronousError: any;
  medicalDeclaration: any[];
  onBack: () => any;
  signpost: ISignpostState;
  salesChannel: string;
  actions: {
    quote: IQuoteActions;
    submit(name: string);
  }
  verisk: IVeriskState;

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

interface IMedicalDeclarationState {
  isScreening: boolean;
  isTerminal: boolean;
  isAnyModalOpen: boolean;
  unableToProvideQuote: boolean;
  isSubmitting: boolean;
}

class MedicalDeclarationQuestions extends React.Component<IMedicalDeclarationQuestionsProps, IMedicalDeclarationState> {

  public state = {
    isScreening: false,
    isTerminal: false,
    isAnyModalOpen: false,
    unableToProvideQuote: false,
    isSubmitting: false,
  };

  constructor(props) {
    super(props);

    const { change, medicalDeclaration, salesChannel } = props;

    if (!isEmpty(medicalDeclaration)) {
      const mappedQuestions = mapMedicalQuestions(salesChannel, medicalDeclaration);
      change('medicalDeclaration', mappedQuestions);
    }
    scrollToTop();
  }


  private shouldShowScreeningSection = () => {
    const { medicalDeclaration } = this.props;

    if (!medicalDeclaration) {
      return false;
    }

    const questions = filter(medicalDeclaration, (question) => question.type === 'question');
    const unanswered = filter(questions, (question) =>
      question.answer === null && question.question_type === 'boolean',
    );
    const trueAnswers = filter(questions, (question) => question.answer === true &&
      !get(question, 'should_not_screen', false));
    return unanswered.length === 0 && trueAnswers.length > 0;
  }

  private setIsScreening = (isScreening: boolean) => {
    this.setState({
      isScreening,
    });
  }

  private handleSubmitForm = () => {
    this.setState({ isSubmitting: true })
    this.checkIfConditionWasDeclared();
  }

  private checkIfConditionWasDeclared = () => {
    const { travellers, medicalDeclaration } = this.props;

    const questions = filter(medicalDeclaration, (question) => question.type === 'question');
    const unanswered = filter(questions, (question) =>
      question.answer === null && question.question_type === 'boolean',
    );
    const trueAnswers = filter(questions, (question) => question.answer === true &&
      !get(question, 'should_not_screen', false));
    const travellerScreening = filter(travellers, (traveller) => traveller.screening);
    const travellersLength = travellers.length;
    const shouldScreen = unanswered.length === 0 && trueAnswers.length > 0;

    if (!shouldScreen) {
      this.checkForErrorsBeforeSubmit();
    }

    if (shouldScreen && travellerScreening.length === travellersLength) {
      this.checkForErrorsBeforeSubmit();
    }

    if (shouldScreen && travellerScreening.length < travellersLength && travellerScreening.length === 0) {
      this.setState({ isAnyModalOpen: true });
      confirm('Finish Medical Screening', {
        description: (
          <div>
            <h4>Warning - You cannot continue without declaring a medical condition.</h4>
            Please click on 'Add Conditions' button to add your conditions.
            <br/>
          </div>
        ),
        done: () => {
          this.setState({ isAnyModalOpen: false, isSubmitting: false });
          return;
        },
        close: () => {
          this.setState({ isAnyModalOpen: false, isSubmitting: false });
          return;
        },
      });
    }

    if (shouldScreen && travellerScreening.length < travellersLength && travellerScreening.length > 0) {
      this.checkForErrorsBeforeSubmit();
    }
  }

  private checkForErrorsBeforeSubmit = () => {
    const { actions, synchronousError, change } = this.props;
    Promise.resolve(actions.quote.calculate(null, true)).then(() => {
      change('schemeId', '');
      Promise.resolve(actions.submit('quote')).then(() => {
        this.setState({ isSubmitting: false })
        if (synchronousError && !isEmpty(synchronousError)) {
          window.scrollTo(0, 0);
        }
      });
    })
  }

  render() {
    const {
      handleSubmit,
      valid,
      onBack,
      submitFailed,
      i18n,
      change,
      product,
      medicalDeclaration,
      renderDefinition,
      verisk
    } = this.props

    const { isScreening, isSubmitting } = this.state;

    const annieMedicalText = (
      <div>
        <div>This information will be used to assess whether we are able to offer you quote.</div>
        <div style={{ marginTop: '10px' }}>If you are not sure what condition you or named travellers were seen for,
          check the below questions with
          your GP Surgery to ensure you make an accurate declaration when buying your policy. If you incorrectly declare
          medical conditions to us, this may affect any claim you make on the policy.
        </div>
      </div>
    )

    return (
      <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">

        <div className="steps-container">

          {submitFailed && !valid && (
            <div className="header-title-container">
              {submitFailed && !valid && (
                <ValidationFailed/>
              )}
            </div>
          )}

          <InfoAnnie title={'We have some questions about your medical history.'}
                     text={annieMedicalText}/>

          <MedicalQuestionCards
            medicalDeclaration={medicalDeclaration}
            renderDefinition={renderDefinition}
            {...this.props}
          />

          <div className="medical-condition-container" hidden={!this.shouldShowScreeningSection()}>
            <div className="section-title">
              <h2>Your medical condition(s)</h2>
            </div>

            <FieldArray
              name="travellers"
              i18n={i18n}
              change={change}
              component={TravellersMedical}
              questions={get(product, 'metadata.medical_declaration_questions')}
              setIsScreening={this.setIsScreening}
              rerenderOnEveryChange={true}
            />
          </div>

          <WarningMessages warningMessage={verisk?.errors}/>

          <div style={{ marginTop: '50px' }} hidden={isScreening === true}>
            <QuoteStepButtons
              submitting={isSubmitting}
              hasBackButton={true}
              submitFailed={submitFailed}
              submitButtonType={'button'}
              callback={this.handleSubmitForm}
              handleBack={onBack}
              valid={valid}
            />
          </div>

        </div>
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const quoteState = state.quote;
      const travellers = selector(state, 'travellers');
      const quotes = selector(state, 'quotes');
      const medicalDeclaration = selector(state, 'medicalDeclaration');
      const salesChannel = selector(state, 'salesChannel');
      const synchronousError = getFormSyncErrors('quote')(state);
      return {
        auth: state.auth,
        signpost: state.quote.signpost,
        verisk: state.verisk,
        travellers,
        quotes,
        quoteState,
        medicalDeclaration,
        synchronousError,
        salesChannel
      };
    },
    (dispatch) => ({
      actions: {
        submit: bindActionCreators(submit, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(MedicalDeclarationQuestions);
